import { Box, CircularProgress, DialogContent, Divider, List, Typography } from "@mui/material"
import { ArchiveOutlined, SearchOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"

import { useProductSearch } from "../../hooks/useProductSearch"
import type { AddedProduct, NewRequestFormValues } from "../../types"

import { AddedProductCard } from "./AddedProductCard"
import { ConfirmedHeaderSummary } from "./ConfirmedHeaderSummary"
import { NewRequestStepActions } from "./NewRequestStepActions"
import { ProductResultItem } from "./ProductResultItem"
import { ProductSearchSuggestion } from "./ProductSearchSuggestion"
import { StepSectionLabel } from "./StepSectionLabel"

import { Button } from "@/components/common/Button"

interface ProductSearchStepProps {
  formValues: NewRequestFormValues
  onCancel: () => void
  onContinue: (products: AddedProduct[]) => void
}

export const ProductSearchStep: FC<ProductSearchStepProps> = ({ formValues, onCancel, onContinue }) => {
  const {
    searchInputValue,
    setSearchInputValue,
    addedProducts,
    addedProductIds,
    visibleResults,
    isFetching,
    showSearchResults,
    canFinalize,
    handleSearch,
    handleProductSuggestionSelect,
    handleSearchClear,
    handleProductAdd,
    handleProductRemove,
    handleRequestQuoteChange,
    handleExpandToggle,
  } = useProductSearch()

  return (
    <>
      <DialogContent className='flex flex-col gap-6 px-8 py-6'>
        <ConfirmedHeaderSummary formValues={formValues} />

        {addedProducts.length > 0 && (
          <Box className='flex flex-col gap-4'>
            {addedProducts.map((addedProduct) => (
              <AddedProductCard
                key={addedProduct.product.productId}
                addedProduct={addedProduct}
                onRemove={() => handleProductRemove(addedProduct.product.productId)}
                onRequestQuoteChange={(checked) => handleRequestQuoteChange(addedProduct.product.productId, checked)}
                onExpandToggle={() => handleExpandToggle(addedProduct.product.productId)}
              />
            ))}
          </Box>
        )}

        <StepSectionLabel icon={<ArchiveOutlined />} label='Search for product to add' />

        <Box className='flex gap-2'>
          <ProductSearchSuggestion
            searchInputValue={searchInputValue}
            onSearchInputChange={setSearchInputValue}
            onSearch={handleSearch}
            onProductSelect={handleProductSuggestionSelect}
            onClear={handleSearchClear}
            excludedProductIds={addedProductIds}
          />
          <Button
            appearance='contained'
            startIcon={<SearchOutlined />}
            onClick={() => handleSearch(searchInputValue)}
            disabled={searchInputValue.trim().length < 2}
            data-testid='sales.product-search-step.search-button'
          >
            Search
          </Button>
        </Box>

        {showSearchResults && isFetching && visibleResults.length === 0 && (
          <Box className='flex justify-center py-2'>
            <CircularProgress size={24} />
          </Box>
        )}

        {showSearchResults && !isFetching && visibleResults.length === 0 && (
          <Typography className='text-sm text-gray-500'>No products found. Try a different search term.</Typography>
        )}

        {showSearchResults && visibleResults.length > 0 && (
          <>
            <Typography className='mt-2 inline-flex align-middle text-sm leading-6'>
              <Typography component='span' className='inline-flex font-semibold text-gray-800'>
                {visibleResults.length} results
              </Typography>
              , Select one to add to the request
            </Typography>
            <Box className='max-h-80 overflow-hidden overflow-y-auto rounded border border-gray-300'>
              <List disablePadding>
                {visibleResults.map((product, index) => (
                  <Box key={product.productId}>
                    {index > 0 && <Divider />}
                    <ProductResultItem product={product} selected={false} onSelect={handleProductAdd} />
                  </Box>
                ))}
              </List>
            </Box>
          </>
        )}
      </DialogContent>

      <NewRequestStepActions
        onCancel={onCancel}
        onContinue={() => onContinue(addedProducts)}
        continueLabel='Finalize'
        continueDisabled={!canFinalize}
        cancelTestId='sales.product-search-step.cancel-button'
        continueTestId='sales.product-search-step.finalize-button'
      />
    </>
  )
}
